import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '@/lib/api';
import { resetPreferenceBaselines } from '@/lib/preferences-store';
import { resetColumnLayoutBaselines } from '@/lib/use-column-layout';
import { AuthContext, type AuthContextValue } from '@/lib/auth-context';
import {
  createAbility,
  EMPTY_PERMISSIONS,
  toPermissionState,
  type PermissionState,
} from '@/lib/permissions';
import {
  activeAccessToken,
  currentAccessExpiry,
  endSupportSessionLocally,
  isSupportSessionLive,
  onSessionExpired,
  onSupportSessionEnded,
  refreshSession,
  revokeRefreshToken,
  supportDeadlineMs,
  supportSessionRemainingMs as supportRemainingMs,
  supportSessionStorage,
  tokenStorage,
  type StoredSupportSession,
  type SupportSessionInfo,
} from '@/lib/session';
import type { User } from '@/lib/types';

/**
 * Refresh this many seconds before the access token expires.
 *
 * Reactive-only refresh means every in-flight request 401s at the same instant
 * and then races to rotate the same single-use token. The server's grace
 * window makes that recoverable rather than catastrophic now, but rotating
 * early from a quiet moment still keeps it off the common path.
 *
 * Note the timer is usually academic: production access tokens last 5.28 days,
 * so few tabs live long enough to reach it and the 401 path does the real
 * work. That is also why the delay is clamped — and why the expiry comes from
 * the server's `expiresIn` rather than the backend repo's 15-minute default,
 * which production does not use.
 */
const REFRESH_LEAD_SECONDS = 60;
/** setTimeout overflows past ~24.8 days; nothing here is close, but clamp anyway. */
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * The public half of a stored support session.
 *
 * Projected rather than passed straight through, so the access token and the
 * local deadline stay out of the context and cannot end up on a screen or in a
 * component's props by accident.
 */
function infoOf(stored: StoredSupportSession): SupportSessionInfo {
  const { actingAs, startedBy, expiresAt, reason } = stored;
  return { actingAs, startedBy, expiresAt, reason };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<PermissionState>(EMPTY_PERMISSIONS);
  // Only "loading" if there is actually a token to exchange — otherwise we
  // already know the answer and can render the logged-out UI immediately.
  // `activeAccessToken()` rather than `tokenStorage.get()`: a reload in the
  // middle of a support session must resume that session, not silently drop
  // back to the super admin's own account under the same URL.
  const [loading, setLoading] = useState(() => Boolean(activeAccessToken()));

  /*
   * The support session, held as the STORED record rather than the public
   * shape, because the expiry timer below needs `deadlineMs` and the
   * "tell the server" call needs the token. `supportSession` on the context is
   * the projection.
   *
   * Seeded from storage so a reload resumes rather than restarts.
   */
  const [supportRecord, setSupportRecord] = useState<StoredSupportSession | null>(() => {
    const stored = supportSessionStorage.get();
    return isSupportSessionLive(stored, Date.now()) ? stored : null;
  });

  /*
   * The super admin's OWN identity, parked for the duration.
   *
   * ⚠️ This is a cache of something already read from the wire, never a
   * substitute for reading it — it holds exactly the `user` and
   * `PermissionState` this tab had loaded a moment before the switch, which is
   * what would still have been in memory had the support session never
   * happened. Restoring from it makes switching back instant and, more
   * importantly, makes it impossible for the target's permission list to
   * outlive the session: the restore is a single assignment of a matched pair,
   * not two independent fetches that could half-land.
   *
   * Null after a reload mid-session — this tab never loaded the super admin's
   * own identity — which `handleSupportEnded` treats as its cold path.
   */
  const ownIdentity = useRef<{ user: User; permissions: PermissionState } | null>(null);

  /*
   * Bumped whenever a new access token is stored, to re-arm the proactive
   * refresh timer against the new expiry. The token itself is not state: it
   * lives in storage, and mirroring it into React would mean re-rendering the
   * whole tree on every rotation for no visual change.
   */
  const [tokenEpoch, setTokenEpoch] = useState(0);
  const bumpToken = useCallback(() => setTokenEpoch((n) => n + 1), []);

  const clearSession = useCallback(() => {
    tokenStorage.clear();
    /*
     * A support session hangs off the base one and cannot outlive it. Cleared
     * here as well as in `session.ts`'s own `endSession()`, because this
     * callback is reached from `logout()` too, which does not go through there.
     */
    supportSessionStorage.clear();
    setSupportRecord(null);
    ownIdentity.current = null;
    setUser(null);
    setPermissions(EMPTY_PERMISSIONS);
    /*
     * ⚠️ **Dropped on EVERY identity change, not just sign-out** — here, and on
     * both ends of a support session. That is the whole point: a support switch
     * does NOT unmount the page you are standing on, so anything cached against
     * "the current user" without being keyed BY the user survives the switch
     * and is then read as belonging to the person you are now acting as.
     *
     * The saved-filter baselines record which preference keys the server holds
     * for each page. They cannot be keyed by user, because `/preferences` has
     * no concept of another person's document — there is no id to key them
     * with. Left in place across a switch, the first filter change made while
     * acting as somebody else sends `null` for the OPERATOR's keys against the
     * TARGET's document, deleting saved filters that were never theirs. A
     * `200`, and nothing to restore from.
     */
    resetPreferenceBaselines();
    /*
     * ⚠️ **The two resets travel together, at all THREE identity changes.** The
     * column-layout baselines are keyed by SCOPE for the same reason and with
     * the same consequence: left in place across a switch, the first layout
     * change made while acting as somebody else sends `null` for the OPERATOR's
     * keys against the TARGET's document. Both are called here, at the start of
     * a support session, and at its end.
     *
     * A store this is forgotten at does not fail — it deletes somebody else's
     * saved state with a `200` — so a THIRD baselined store must be added at all
     * three sites, not just this one.
     */
    resetColumnLayoutBaselines();
  }, []);

  /**
   * Load the advisory permission list. Never throws: a failure downgrades the
   * UI to role-only gating rather than blocking sign-in, because the list can
   * only narrow what the role already allows.
   */
  const loadPermissions = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await api.permissionsMe(signal);
      if (signal?.aborted) return;
      setPermissions(toPermissionState(response.data));
    } catch {
      if (signal?.aborted) return;
      setPermissions({ ...EMPTY_PERMISSIONS, loaded: true, failed: true });
    }
  }, []);

  /**
   * Load WHO WE ARE ACTING AS, and what they may do — always as one unit.
   *
   * ## ⚠️ This pairing is the whole support-session correctness argument
   *
   * `/permissions/me` is fetched once per session and never invalidated, which
   * is fine while an app has one identity and catastrophic the moment it has
   * two. A support session that loaded the target's `user` but kept the super
   * admin's cached permission list would render **the super admin's access
   * under the target's name** — every control they hold and the target does
   * not, lit up, on a screen whose entire purpose is to show what the target
   * sees. Nothing would fail, nothing would log, and the answer would be wrong
   * in the reassuring direction.
   *
   * So identity is never assembled from two places. Both halves come off the
   * wire under whatever token is currently active, in one function, and the
   * only callers are the three moments the acting identity can change: mount,
   * the start of a support session, and its end.
   *
   * ⚠️ **The target's identity comes from `/auth/me` under the support token —
   * never from the start response's `actingAs`.** `actingAs` is a label for the
   * banner. Reading it as the signed-in user would mean trusting the client's
   * idea of who the token belongs to, and would go on working for a while if
   * the two ever disagreed.
   */
  const loadIdentity = useCallback(
    async (signal?: AbortSignal) => {
      const response = await api.me(signal);
      if (signal?.aborted) return;
      setUser(response.data);
      await loadPermissions(signal);
    },
    [loadPermissions],
  );

  // The session can end from anywhere — a background refresh, another tab's
  // rotation — not only from a request a component was awaiting.
  useEffect(() => onSessionExpired(clearSession), [clearSession]);

  /*
   * A support session ended: the clock ran out, the server refused the token,
   * or somebody pressed the button.
   *
   * ⚠️ **All three endings arrive here, and that is deliberate.** Two of them
   * happen where nobody is awaiting anything — a timer, and a 401 inside the
   * transport — so a return value could not carry them. Funnelling the third
   * through the same event means there is exactly ONE piece of code that
   * restores the super admin, rather than a button path and a background path
   * that can drift until only one of them clears the permission list.
   */
  const handleSupportEnded = useCallback(() => {
    /*
     * 🔴 **THE BASELINE RESET BELONGS IN THE FUNNEL, AND IT WAS OUTSIDE IT.**
     *
     * A support session has THREE endings: the button, the expiry timer, and a
     * 401 on the support token. This function's own header says they are
     * funnelled here precisely so there is exactly ONE piece of code that
     * restores the super admin "rather than a button path and a background path
     * that can drift" — and the baseline reset is the thing that had drifted.
     * It sat on the BUTTON path only, so after a timeout or a 401 unwind the
     * operator came back to their own account still holding baselines built
     * under the TARGET's document.
     *
     * What that costs is the exact hazard `resetPreferenceBaselines` exists to
     * prevent, arriving from the other side: the next save sends an explicit
     * `null` for every key the TARGET had and the OPERATOR does not, deleting
     * saved filters and column layouts that were never theirs. It answers
     * `200`. Nothing fails.
     *
     * ⚠️ Both resets are called here AND left on the button path. They are
     * idempotent — clearing an already-clear map costs nothing — and removing
     * the direct calls would make the button's correctness depend on an event
     * listener firing, which is a longer chain than this needs.
     *
     * ⚠️ **A THIRD BASELINED STORE MUST BE ADDED IN ALL FOUR PLACES.** That is
     * the argument for folding the two into one `resetIdentityScopedBaselines()`
     * — deliberately not done here, because it needs a new shared module (each
     * store importing the other couples two independent things) and this tree
     * currently has agents holding files across it.
     */
    resetPreferenceBaselines();
    resetColumnLayoutBaselines();

    setSupportRecord(null);
    const own = ownIdentity.current;
    ownIdentity.current = null;

    if (own) {
      /*
       * The ordinary path. Restore the pair we parked, so the switch back is
       * instant and the target's permissions cannot survive it, then re-read
       * from the wire to pick up anything that changed while we were away. A
       * failure there leaves the restored pair in place — the base token was
       * never touched, so a transient error is not evidence the session ended.
       */
      setUser(own.user);
      setPermissions(own.permissions);
      void loadIdentity().catch(() => undefined);
      return;
    }

    /*
     * The cold path: this tab was reloaded during the session, so it never
     * loaded the super admin's own identity and has nothing to restore.
     *
     * It goes back to "checking your session" rather than guessing. Showing the
     * target's name for the moment the fetch takes would be the same defect the
     * pairing above exists to prevent, one identity later — and `RequireAuth`
     * renders a spinner while `loading`, so nothing redirects to /login on the
     * way through.
     */
    setLoading(true);
    setUser(null);
    setPermissions(EMPTY_PERMISSIONS);
    loadIdentity()
      .catch(() => clearSession())
      .finally(() => setLoading(false));
  }, [loadIdentity, clearSession]);

  useEffect(() => onSupportSessionEnded(handleSupportEnded), [handleSupportEnded]);

  /*
   * End the support session when its window closes.
   *
   * This is the LOCAL half of expiry detection and it is deliberately not the
   * authoritative one — see `sendWithRetry` in `api.ts`, where a 401 on the
   * support token ends the session on the server's own say-so. The two answer
   * different needs: the server settles whether the token still works, and this
   * timer settles whether the BANNER is still telling the truth. Without it, a
   * quiet screen making no requests would count down past zero and keep
   * claiming to be somebody else for as long as nobody clicked anything.
   *
   * The deadline is a local one computed from the server's remaining DURATION
   * (see `supportDeadlineMs`), so it does not inherit clock skew — and where
   * the two disagree anyway, the server's 401 wins, because this timer can only
   * ever end a session early, never extend one.
   */
  useEffect(() => {
    if (!supportRecord) return;

    const delay = Math.min(Math.max(supportRecord.deadlineMs - Date.now(), 0), MAX_TIMEOUT_MS);
    const timer = window.setTimeout(() => {
      // Fires `handleSupportEnded` through the listener above, like every other
      // ending. Nothing about the restore is duplicated here.
      endSupportSessionLocally();
    }, delay);

    return () => window.clearTimeout(timer);
  }, [supportRecord]);

  // On mount, exchange any stored token for the current user. A stale or
  // expired token simply clears itself rather than wedging the app.
  useEffect(() => {
    // The ACTIVE token — a support session resumes across a reload.
    if (!activeAccessToken()) return;

    const controller = new AbortController();
    /*
     * oxlint traces `loadIdentity` — a local callback — and sees the `setUser`
     * inside it, but that call sits behind `await api.me(...)` and is never
     * synchronous. It did not flag the equivalent `api.me(...).then(setUser)`
     * this replaced, only because `api.me` is imported and therefore opaque to
     * it. Inlining the fetch again would silence it and would also give this
     * file a SECOND place that loads an identity — which is precisely what the
     * comment on `loadIdentity` exists to prevent, and how the target's
     * permissions would eventually outlive their session.
     */
    // oxlint-disable-next-line react/set-state-in-effect
    loadIdentity(controller.signal)
      .catch(() => {
        if (controller.signal.aborted) return;
        // `request()` already tried a refresh and failed, so this token is
        // genuinely spent. /auth/me also 404s if the account was deleted.
        clearSession();
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [clearSession, loadIdentity]);

  /*
   * Rotate ahead of expiry.
   *
   * `refreshSession()` is single-flight within the tab, and handles a lost
   * cross-tab race itself. A null result means "not now" — either the session
   * ended (listeners have already fired) or the attempt was inconclusive — and
   * in neither case is retrying from here correct.
   *
   * ⚠️ **Not suppressed during a support session, and that is correct.** It
   * rotates the BASE pair — the super admin's own, sitting untouched under its
   * own keys — and writes it back to those keys alone. The support token is
   * neither read nor replaced, and `currentAccessExpiry()` reads the base
   * expiry, so this schedules against the right clock either way. In practice
   * it never fires here at all: base tokens last 5.28 days and a support
   * session lasts thirty minutes.
   */
  const scheduled = useRef<number | null>(null);
  useEffect(() => {
    if (!user) return;
    const exp = currentAccessExpiry();
    if (exp === null) return;

    const delay = Math.min(
      Math.max((exp - REFRESH_LEAD_SECONDS) * 1000 - Date.now(), 0),
      MAX_TIMEOUT_MS,
    );

    scheduled.current = window.setTimeout(() => {
      void refreshSession().then((token) => {
        if (token) bumpToken();
      });
    }, delay);

    return () => {
      if (scheduled.current !== null) window.clearTimeout(scheduled.current);
    };
  }, [user, tokenEpoch, bumpToken]);

  const login = useCallback(
    async (email: string, password: string, remember: boolean) => {
      const { data } = await api.login(email, password);
      /*
       * Signing in is a fresh identity, so nothing may survive it. A support
       * session should already be gone by here — `clearSession()` drops it on
       * every sign-out and on every expiry — but a stale blob would shadow the
       * token we are about to store, and the failure would be someone signing
       * in and landing in a stranger's account. Cheap, and structural.
       */
      supportSessionStorage.clear();
      setSupportRecord(null);
      ownIdentity.current = null;
      tokenStorage.set(data.accessToken, data.refreshToken, remember, data.expiresIn);
      setUser(data.user);
      bumpToken();
      // Awaited so the first screen after sign-in renders with real gating
      // rather than flickering controls in and out.
      await loadPermissions();
    },
    [bumpToken, loadPermissions],
  );

  const logout = useCallback(() => {
    /*
     * Read the token first, then clear, then revoke — the redirect must not
     * wait on the network. `/auth/logout` answers 204 for any well-formed
     * body, valid token or not, so a failure only ever means the request did
     * not arrive; it never means the session is still live and we should stay
     * signed in. The cost of a missed revoke is a refresh token that stays
     * usable until it expires, which is strictly better than a sign-out button
     * that appears not to work.
     */
    const refreshToken = tokenStorage.getRefresh();
    const support = supportSessionStorage.get();
    clearSession();
    revokeRefreshToken(refreshToken);
    // Signing out mid-support-session closes both. Fire-and-forget, same
    // reasoning as the revoke above; the support token expires by itself anyway.
    if (support) void api.endSupportSession(support.accessToken).catch(() => undefined);
  }, [clearSession]);

  /**
   * Start acting as another user.
   *
   * The ORDER here is the whole safety property, and each step buys something:
   *
   *   1. refuse a nested session — the base token is what a support session is
   *      layered over, and a second layer would shadow the first with nothing
   *      able to unwind it;
   *   2. get a deadline, or **refuse to start**. A support session nobody can
   *      compute the end of is exactly the unbounded access the 30-minute rule
   *      forbids;
   *   3. park our own identity BEFORE anything switches, while it is still ours
   *      to read;
   *   4. write storage and React state **together**, so the banner and the
   *      attached token can never disagree — a banner missing while the support
   *      token is live means the super admin acts on a stranger's data thinking
   *      it is their own, and the reverse means they write to their OWN account
   *      believing they are somebody else. Both are silent, and the second is
   *      worse;
   *   5. blank the permission list before loading the target's, so no render
   *      can pair the target's name with the super admin's access;
   *   6. and unwind ALL of it if the identity does not load.
   *
   * Resolves only once the target's identity and permissions are in place, so a
   * caller may navigate on the resolved promise.
   */
  const startSupportSession = useCallback(
    async (userId: number, reason: string) => {
      if (supportSessionStorage.get() !== null) {
        throw new Error('A support session is already running. End it before starting another.');
      }

      const { data } = await api.startSupportSession({ userId, reason });

      /*
       * ⚠️ The duration is on the ENVELOPE and the deadline is on the RECORD —
       * two levels, and the pair has to be assembled by hand. `expiresIn` is
       * what the deadline is derived from; `session.expiresAt` is the fallback
       * only, and is passed so `supportDeadlineMs` keeps its documented
       * degradation for an older server rather than being handed a half-object
       * that can only ever take one branch.
       */
      const deadlineMs = supportDeadlineMs(
        { expiresIn: data.expiresIn, expiresAt: data.session.expiresAt },
        Date.now(),
      );
      if (deadlineMs === null) {
        /*
         * Fail closed, loudly. The server created a session we cannot bound, so
         * close it rather than hold a token with no end — a start that visibly
         * refuses gets reported; one that quietly never expires does not.
         */
        void api.endSupportSession(data.accessToken).catch(() => undefined);
        throw new Error(
          'The server did not say when this support session ends, so it was not started.',
        );
      }

      const stored: StoredSupportSession = {
        accessToken: data.accessToken,
        /*
         * ⚠️ **The target's NAME comes from `data.user`, not from
         * `data.session.actingAs`**, whose parties are `{id, email}` and carry
         * none. This is a LABEL for the banner and nothing else — the acting
         * identity is loaded from `/auth/me` under the support token below.
         */
        actingAs: { id: data.user.id, name: data.user.name, email: data.user.email },
        // `{id, email}`, id nullable. Stored for the trail; the banner does not
        // render it — see the note on the banner in `AppShell`.
        startedBy: data.session.startedBy,
        expiresAt: data.session.expiresAt,
        // The server's own record of the reason, not the string that was typed.
        reason: data.session.reason,
        deadlineMs,
      };

      if (user) ownIdentity.current = { user, permissions };

      supportSessionStorage.set(stored);
      setSupportRecord(stored);
      setPermissions(EMPTY_PERMISSIONS);
      // From here every request is the TARGET's. See `clearSession` for what
      // survives a switch that nothing unmounts, and what it would delete.
      resetPreferenceBaselines();
      resetColumnLayoutBaselines();

      try {
        await loadIdentity();
      } catch (error) {
        /*
         * We hold a token for somebody we could not identify. Unwind completely
         * — storage, state, and the server's copy — and put the super admin
         * back where they were. `handleSupportEnded` owns the restore, so it is
         * reached through the same event every other ending uses.
         */
        supportSessionStorage.clear();
        void api.endSupportSession(stored.accessToken).catch(() => undefined);
        handleSupportEnded();
        throw error;
      }
    },
    [user, permissions, loadIdentity, handleSupportEnded],
  );

  /**
   * Stop acting as another user.
   *
   * ⚠️ **Local first, network second, and never the other way round.** The
   * super admin's own tokens were never touched, so returning to their account
   * is a delete of one key plus a restore of state already in hand — it cannot
   * fail, and it must not be made to depend on a request that can. Telling the
   * server is fire-and-forget, exactly as `logout` is, and for the same reason:
   * the worst case is a session the server holds open until it expires on its
   * own, which beats a button that appears not to work.
   */
  const endSupportSession = useCallback(() => {
    // Take the token before clearing — after this there is nothing to send with.
    const ended = endSupportSessionLocally();
    // The acting identity just changed back. See `clearSession`.
    resetPreferenceBaselines();
    resetColumnLayoutBaselines();
    if (ended) void api.endSupportSession(ended.accessToken).catch(() => undefined);
  }, []);

  const ability = useMemo(() => createAbility(user, permissions), [user, permissions]);

  /*
   * Deliberately does NOT clear the user or the token on failure, unlike the
   * mount effect above. That one is deciding whether a stored token is still
   * good; this one is refreshing a session already known to be valid, so a
   * transient error must not sign anyone out mid-edit.
   */
  const refreshUser = useCallback(async () => {
    try {
      const response = await api.me();
      setUser(response.data);
    } catch {
      // Keep the current user; the next real request will surface a dead session.
    }
  }, []);

  /*
   * The public projection: the metadata, without the token or the local
   * deadline. Neither belongs on a screen, and keeping them off the context is
   * how they stay off one.
   */
  const supportSession = useMemo(
    () => (supportRecord ? infoOf(supportRecord) : null),
    [supportRecord],
  );

  /**
   * How long is left, measured against the record this provider already holds.
   *
   * ⚠️ **This is why the projection above can keep `deadlineMs` off the
   * context.** The countdown is the only consumer of it, and answering the
   * question here means the deadline never has to be published — the surface
   * that renders a countdown asks, rather than going back to `localStorage`
   * for a second copy of the session.
   *
   * ⚠️ **Skew-free by inheritance, not by care taken here.** `deadlineMs` was
   * computed once from the server's `expiresIn` DURATION at start; nothing on
   * this path parses `expiresAt`, and nothing should be added that does.
   *
   * ⚠️ **`NaN` when there is no live record**, never `0` — see the context.
   * `supportSessionRemainingMs` floors at 0 for a record that has run out,
   * which is a genuine "Expired"; an absent record is "we cannot say".
   *
   * Depends on `supportRecord` alone, so the identity is stable between
   * sessions and a banner's timer effect is not torn down on every render.
   */
  const remainingMs = useCallback(
    () => (supportRecord === null ? Number.NaN : supportRemainingMs(supportRecord, Date.now())),
    [supportRecord],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      ability,
      login,
      logout,
      refreshUser,
      supportSession,
      supportSessionRemainingMs: remainingMs,
      startSupportSession,
      endSupportSession,
    }),
    [
      user,
      loading,
      ability,
      login,
      logout,
      refreshUser,
      supportSession,
      remainingMs,
      startSupportSession,
      endSupportSession,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
